import * as React from 'react';
import { Box } from '@mui/material';
import { MetricWindow, WindowKey } from '../types';
import {
  EMPHASIS_WINDOW,
  Tone,
  WINDOW_LABELS,
  WINDOW_ORDER,
  fmtMoney,
  fmtPremium,
  fmtRoas,
  premiumTone,
  trueTone,
  windowVal,
} from '../utils';

export type MetricKind = 'money' | 'premium' | 'roas';
export type ColorAs = 'true' | 'premium' | undefined;

const toneBg: Record<Tone, string> = {
  good: 'var(--color-background-success, #e9f7ef)',
  bad: 'var(--color-background-danger, #fdecec)',
  '': 'transparent',
};
const toneFg: Record<Tone, string> = {
  good: 'var(--color-text-success, #1d7a4d)',
  bad: 'var(--color-text-danger, #c0392b)',
  '': 'inherit',
};

function fmt(kind: MetricKind, v: number | null): string {
  if (kind === 'money') return fmtMoney(v);
  if (kind === 'premium') return fmtPremium(v);
  return fmtRoas(v);
}

// One column per metric; the cell shows all four windows (T / Y / 7d / 14d)
// spread evenly across the column width. Today is emphasized (bold). Color tone
// applies only to the decision metrics (True, Premium).
export default function MetricCell({
  windows,
  metricKey,
  kind,
  colorAs,
  target = 0,
  premiumMax = 0.15,
}: {
  windows: Partial<Record<WindowKey, MetricWindow>>;
  metricKey: keyof MetricWindow;
  kind: MetricKind;
  colorAs?: ColorAs;
  target?: number;
  premiumMax?: number;
}) {
  return (
    <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
      {WINDOW_ORDER.map(w => {
        const v = windowVal(windows, w, metricKey);
        const tone: Tone =
          colorAs === 'true'
            ? trueTone(v, target)
            : colorAs === 'premium'
            ? premiumTone(v, premiumMax)
            : '';
        const emphasized = w === EMPHASIS_WINDOW;
        return (
          <Box
            key={w}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              textAlign: 'center',
              borderRadius: '6px',
              py: '1px',
              backgroundColor: toneBg[tone],
            }}
          >
            <Box sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.2 }}>
              {WINDOW_LABELS[w]}
            </Box>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: emphasized ? 600 : 400,
                color: tone ? toneFg[tone] : 'text.primary',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
              }}
            >
              {fmt(kind, v)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
